import { Card, OpenClosedIndicator } from "@greeter/matter-v2";
import { MinPriceProgressBarContent } from "./MinPriceProgressBarCard";
import { AnimatePresence, motion } from "motion/react";
import { Bundle, BundleLineVariantId, Quantity } from "@greeter/core";
import { formatMoney } from "@greeter/formatters";
import {
  dropInOutSmooshFromBottom,
  dropInOutSmooshFromTop,
} from "@greeter/motion";
import { classNames } from "@greeter/util";
import { IonIcon } from "@ionic/react";
import { alertCircle } from "ionicons/icons";
import { BundleForm } from "../../models";
import { BundleFormSummary } from "../../BundleFormSummary";

import css from "./PickerOverviewDisplay.module.scss";

export type Position = number;

export type BundleChanges = Map<
  BundleLineVariantId,
  Map<Position, BundleLineVariantId>
>;

export type AggregatedQuantityBundleChange = Map<
  BundleLineVariantId,
  Map<BundleLineVariantId, Quantity>
>;

/**
 * Aggregates quantities of changed bundle line variants.
 *
 * This function takes a `BundleChanges` map, which details
 * original bundle variants and their changes at specific positions,
 * and transforms it into an `AggregatedQuantityBundleChange` map.
 * The output map shows, for each original variant, how many times
 * it was changed to a particular new variant ID.
 *
 * @param bundleChanges A Map where the key is the original
 *   `BundleLineVariantId`, and the value is another Map that
 *   associates `Position` with the `BundleLineVariantId` it was
 *   changed to.
 * @returns An `AggregatedQuantityBundleChange` Map summarizing
 *   the total quantity of each new variant ID for a given original
 *   variant ID.
 */
export function aggregateBundleChanges(
  bundleChanges: BundleChanges
): AggregatedQuantityBundleChange {
  const aggregatedMap: AggregatedQuantityBundleChange = new Map();

  for (const [originalVariantId, positionChanges] of bundleChanges.entries()) {
    let innerAggregatedMap = aggregatedMap.get(originalVariantId);

    if (!innerAggregatedMap) {
      innerAggregatedMap = new Map<BundleLineVariantId, Quantity>();
      aggregatedMap.set(originalVariantId, innerAggregatedMap);
    }

    for (const changedToVariantId of positionChanges.values()) {
      innerAggregatedMap.set(
        changedToVariantId,
        (innerAggregatedMap.get(changedToVariantId) || 0) + 1
      );
    }
  }

  return aggregatedMap;
}

export type PickerOverviewDisplayProps = {
  minPrice: number;
  originalBundle: Bundle;
  bundle: BundleForm;

  bundleOriginalPrice: number;
  bundleAdditionalCosts: number;

  cartTotal: number;

  hasChanges?: boolean;
  open?: boolean;
  onToggle?: () => void;

  style?: React.CSSProperties;
};

PickerOverviewDisplay.LogTag = "[PickerOverviewDisplay]";
export function PickerOverviewDisplay({
  open,
  style,
  ...props
}: PickerOverviewDisplayProps) {
  const bundleTotalAndCurrentTotal =
    props.bundleOriginalPrice + props.bundleAdditionalCosts + props.cartTotal;

  return (
    <Card
      layout
      {...classNames(
        css.PickerOverviewDisplay,
        !open && css.Condensed,
        props.hasChanges && "pulse"
      )}
      style={{
        borderRadius: 10 /* DO NOT MOVE TO CSS: In order for motion.dev to work properly and scale borders cleanly, the border must be set directly */,
        height: "auto",
        ...{ "--outline-color-rgb-value": "69, 211, 194" },
        ...style,
      }}
    >
      <Card.Content
        layout="position"
        className={css.Header}
        onClick={props.onToggle}
        style={{ paddingBottom: open ? 0 : undefined }}
        padding={!open ? "sm" : undefined}
      >
        <motion.span layout="position" className={css.ChangeIndicatorContainer}>
          Pakke oversigt
          <AnimatePresence>
            {props.hasChanges && (
              <motion.div
                key="change-indicator"
                {...classNames("pulse", css.ChangeIndicator)}
              />
            )}
          </AnimatePresence>
        </motion.span>

        <motion.div layout="position">
          <OpenClosedIndicator open={open} style={{ margin: 0, height: 30 }} />
        </motion.div>
      </Card.Content>
      <AnimatePresence mode="sync">
        {open && (
          <motion.div layout>
            <Card.Content
              {...dropInOutSmooshFromBottom}
              key="progress-bar"
              layout="position"
            >
              <MinPriceProgressBarContent
                target={props.minPrice}
                value={bundleTotalAndCurrentTotal}
              />
              {props.minPrice - bundleTotalAndCurrentTotal > 0 && (
                <Card variant="yellow" className={css.WarningCard}>
                  <Card.Content padding="sm" className={css.WarningCardContent}>
                    <IonIcon icon={alertCircle} />
                    <p className={css.WarningCardText}>
                      Du mangler {props.minPrice - bundleTotalAndCurrentTotal}{" "}
                      kr. for at nå minimum på dette bord.
                      <br />
                      <br />
                      Du kan tilføje endnu en bordpakke, ændre denne bordpakke
                      eller vælge et andet bord.
                    </p>
                  </Card.Content>
                </Card>
              )}
            </Card.Content>
            <Card.Content
              layout="position"
              key="summary"
              style={{ paddingTop: 0 }}
              {...dropInOutSmooshFromTop}
            >
              <BundleFormSummary bundle={props.bundle} />
              <div className={css.HorizontalLine} />
              <div className={css.Total}>
                <div
                  className={css.TotalLine}
                  style={{ color: "var(--color-yellow)" }}
                >
                  <span>Grundpakke</span>
                  <span>{props.bundleOriginalPrice} kr.</span>
                </div>
                <div
                  className={css.TotalLine}
                  style={{ color: "var(--color-green)" }}
                >
                  <span>Ændringer / Tilføjelser</span>
                  <span>{formatMoney(props.bundleAdditionalCosts)} kr.</span>
                </div>
                <div className={css.TotalLine}>
                  <span style={{ fontWeight: "bold", fontSize: 16 }}>
                    Total
                  </span>
                  <span style={{ fontWeight: "bold", fontSize: 16 }}>
                    {formatMoney(
                      props.bundleOriginalPrice + props.bundleAdditionalCosts
                    )}{" "}
                    kr.
                  </span>
                </div>
              </div>
            </Card.Content>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}
